/**
 * AdminPanel — full system access.
 * - User management table (view, promote, deactivate)
 * - Content moderation queue (approve / reject events)
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useUIStore } from '@/stores/uiStore';
import { ROLES, ROLE_HIERARCHY } from '@/lib/constants';

// ── Data hooks ────────────────────────────────────────────────────────────────
function useUsers() {
  return useQuery({
    queryKey: ['adminUsers'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/api/admin/users', { signal });
      return data;
    },
  });
}

function usePendingEvents() {
  return useQuery({
    queryKey: ['adminPendingEvents'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/api/admin/events/pending', { signal });
      return data;
    },
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────
function useUpdateUserRole() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);
  return useMutation({
    mutationFn: ({ userId, role }) => apiClient.patch(`/api/admin/users/${userId}`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      addToast({ title: 'Role updated', variant: 'success' });
    },
  });
}

function useModeratEvent() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);
  return useMutation({
    mutationFn: ({ eventId, action }) =>
      apiClient.patch(`/api/admin/events/${eventId}`, { status: action }),
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ['adminPendingEvents'] });
      queryClient.invalidateQueries({ queryKey: ['impactEvents'] });
      addToast({ title: `Event ${action}`, variant: 'success' });
    },
  });
}

// ── Tab component ─────────────────────────────────────────────────────────────
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`border-b-2 px-4 py-3 text-sm font-medium transition ${
        active ? 'border-brand-500 text-brand-400' : 'border-transparent text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [tab, setTab] = useState('moderation');
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: pending = [], isLoading: pendingLoading } = usePendingEvents();
  const updateRole = useUpdateUserRole();
  const moderate = useModeratEvent();

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-slate-100">Admin Panel</h1>
        <p className="mt-1 text-sm text-slate-400">Full system access — user management and content moderation</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex border-b border-slate-700">
        <TabButton active={tab === 'moderation'} onClick={() => setTab('moderation')}>
          Moderation Queue
          {pending.length > 0 && (
            <span className="ml-2 rounded-full bg-brand-500 px-1.5 py-0.5 text-xs text-white">
              {pending.length}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
          User Management
        </TabButton>
      </div>

      {/* ── Moderation Queue ──────────────────────────────────────────────── */}
      {tab === 'moderation' && (
        <div className="space-y-3">
          {pendingLoading ? (
            [1, 2].map((i) => <div key={i} className="h-24 animate-skeleton rounded-xl bg-slate-800" />)
          ) : pending.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-700 py-16 text-center">
              <p className="text-slate-400">Queue is clear — all events reviewed.</p>
            </div>
          ) : (
            pending.map((ev) => (
              <div key={ev.id} className="rounded-xl border border-slate-700 bg-impact-overlay p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-100">{ev.title}</h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {ev.location_label} · {ev.event_date} · by {ev.contributor_name}
                    </p>
                    {ev.summary && (
                      <p className="mt-2 text-sm text-slate-400 line-clamp-2">{ev.summary}</p>
                    )}
                    {ev.is_sensitive && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs text-amber-500">
                        🔒 Marked sensitive
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => moderate.mutate({ eventId: ev.id, action: 'approved' })}
                      disabled={moderate.isPending}
                      className="rounded-lg bg-green-600/20 px-3 py-1.5 text-xs font-medium text-green-300 transition hover:bg-green-600/30"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => moderate.mutate({ eventId: ev.id, action: 'rejected' })}
                      disabled={moderate.isPending}
                      className="rounded-lg bg-red-600/20 px-3 py-1.5 text-xs font-medium text-red-400 transition hover:bg-red-600/30"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── User Management ───────────────────────────────────────────────── */}
      {tab === 'users' && (
        <div className="rounded-2xl border border-slate-700 bg-impact-overlay overflow-hidden">
          {usersLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-skeleton rounded-lg bg-slate-800" />)}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Joined</th>
                  <th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-800/40">
                    <td className="px-5 py-3 font-medium text-slate-200">{u.name}</td>
                    <td className="px-5 py-3 text-slate-400">{u.email}</td>
                    <td className="px-5 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => updateRole.mutate({ userId: u.id, role: e.target.value })}
                        className="rounded-lg border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-200 focus:border-brand-500 focus:outline-none capitalize"
                      >
                        {ROLE_HIERARCHY.map((r) => (
                          <option key={r} value={r}>{r.replace('_', ' ')}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-slate-500">{u.created_at?.slice(0, 10)}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => updateRole.mutate({ userId: u.id, role: ROLES.PUBLIC })}
                        className="text-xs text-red-400 hover:underline"
                        disabled={u.role === ROLES.ADMIN}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
